import {
  Dialog,
  Form,
  FormButton,
  Input,
  useNotifications
} from '@ijoin/components';
import {
  AdminRkTransactionExportRowRes,
  getGetAdminRkTransactionExportsQueryKey,
  usePatchAdminRkTransactionExport
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '../types';

type FormValues = {
  carrierResultMessage: string;
  fileIdentifier: string;
};

export const RkTransactionExportEditDialog: FC<{
  onClose: () => void;
  transactionExport: AdminRkTransactionExportRowRes;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormValues>({
    defaultValues: {
      carrierResultMessage: props.transactionExport.carrierResultMessage ?? '',
      fileIdentifier: props.transactionExport.fileIdentifier ?? ''
    }
  });

  const patchExport = usePatchAdminRkTransactionExport<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to update export'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminRkTransactionExportsQueryKey()
        });
        notifications.success('Export updated');
        props.onClose();
      }
    }
  });

  const onSubmit = (data: FormValues) =>
    patchExport.mutate({
      data: {
        carrierResultMessage: data.carrierResultMessage.trim() || null,
        fileIdentifier: data.fileIdentifier.trim() || null
      },
      id: props.transactionExport.id
    });

  return (
    <Dialog
      data-testid='editRkTransactionExportDialog'
      fullWidth
      isOpen
      maxWidth='sm'
      onClose={props.onClose}>
      <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Edit Export</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Input
              data-testid='fileIdentifier'
              format='string'
              fullWidth
              label='File Identifier'
              name='fileIdentifier'
              size='small'
            />
            <Input
              data-testid='carrierResultMessage'
              format='string'
              fullWidth
              label='Carrier Result'
              multiline
              name='carrierResultMessage'
              rows={4}
              size='small'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelEditRkTransactionExport'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='submitEditRkTransactionExport'
            disabled={patchExport.isPending}
            variant='contained'>
            Save
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

RkTransactionExportEditDialog.displayName = 'RkTransactionExportEditDialog';
